import React, { useCallback, useMemo } from 'react';
import { List, ListImperativeAPI, RowComponentProps } from 'react-window';

export type TVirtualListRef = ListImperativeAPI;

export interface IChildrenProps<T> {
  /**
   * Index of the rendered row.
   */
  index: number,

  /**
   * Positioning styles that MUST be applied to the row's root element.
   */
  style: React.CSSProperties,

  /**
   * Data item for the rendered row.
   */
  data: T,

  /**
   * Accessibility attributes provided by react-window (optional to spread onto the row's root element).
   */
  ariaAttributes: RowComponentProps['ariaAttributes'],
}

interface IProps<T> {
  /**
   * The data array that will be rendered in the virtualized list.
   */
  data: T[],

  /**
   * Unique key for each item in the list. Can be either a key from the data object or a function
   * returning a unique identifier.
   */
  itemKey: keyof T | ((item: T) => string | number),

  /**
   * The component used to render each row in the virtualized list.
   * It receives `index`, `style`, and `data` as props.
   */
  children: React.ComponentType<IChildrenProps<T>>,

  /**
   * Width of the virtualized list container. Defaults to `100%`.
   */
  width?: number | string,

  /**
   * Height of the virtualized list container, which determines the number of visible rows.
   */
  height: number | string,

  /**
   * The fixed height of each individual row in the list.
   */
  itemSize: number,

  /**
   * Number of extra rows to render before and after the visible area for smoother scrolling.
   * Defaults to `0`.
   */
  overscanCount?: number,

  /**
   * Called on every scroll of the list's scroll container.
   */
  onScroll?: React.UIEventHandler<HTMLDivElement>,

  /**
   * Imperative API of the list (`element`, `scrollToRow`).
   */
  ref?: React.Ref<TVirtualListRef>,
}

interface IRowProps<T> {
  data: T[],
  rowChildren: React.ComponentType<IChildrenProps<T>>,
}

const Row = <T,>({
  index,
  style,
  ariaAttributes,
  data,
  rowChildren: Children,
}: RowComponentProps<IRowProps<T>>) => (
  <Children
    index={ index }
    style={ style }
    ariaAttributes={ ariaAttributes }
    data={ data[index] }
  />
);

const VirtualList = <T extends Record<string, any>>({
  data,
  itemKey,
  width = '100%',
  height,
  itemSize,
  overscanCount = 0,
  children,
  onScroll,
  ref,
}: IProps<T>) => {
  const rowKey = useCallback((index: number): React.Key => {
    const item = data[index];

    if (typeof itemKey === 'function') {
      return itemKey(item);
    }

    return item[itemKey];
  }, [data, itemKey]);

  const rowProps = useMemo<IRowProps<T>>(() => ({
    data,
    rowChildren: children,
  }), [data, children]);

  return (
    <List<IRowProps<T>>
      listRef={ ref }
      rowCount={ data.length }
      rowHeight={ itemSize }
      overscanCount={ overscanCount }
      rowKey={ rowKey }
      rowComponent={ Row }
      rowProps={ rowProps }
      style={ { width, height } }
      onScroll={ onScroll }
    />
  );
};

export default VirtualList;
